import React from "react";
import MetricInfoIcon from "../ui/MetricInfoIcon";
import type { MetricSample } from "../../hooks/useMetricSamples";
import { HEX, hexToRgba } from "../../lib/colorSystem";
import { formatUSD, toDisplayCurrency } from "@/utils/formatters";
import Tooltip from "../ui/Tooltip";

// =============================================================================
// REVENUE BAR CHART — Option B: Cash Hero + Revenue Context + Goal
// =============================================================================
// Cash is THE number (56px). Revenue appears as a subtitle line.
// Bars show the visual gap. Goal progress bar sits at the bottom.
// =============================================================================

interface RevenueBarChartProps {
  revenueClosed: number;
  cashCollected: number;
  getSamples?: (key: string) => MetricSample[];
  highTicketCash?: number;
  lowTicketCash?: number;
  // R64: low-ticket DOWNSELL cash. When > 0, the "LT" line shows purchase +
  // downsell combined and splits into the two on hover.
  scoLowTicketCash?: number;
  installmentsCash?: number;
  // Goal data (optional — renders progress bar when present)
  goalAmount?: number;
  // This month's cash (org tz). The goal bar uses it; omitted = falls back to cashCollected.
  goalCash?: number;
  daysElapsed?: number;
  daysInMonth?: number;
  monthLabel?: string; // optional — when set, replaces "this month" copy with the month name (e.g., "April 2026")
}

const RevenueBarChart: React.FC<RevenueBarChartProps> = ({
  revenueClosed,
  cashCollected,
  getSamples,
  highTicketCash = 0,
  lowTicketCash = 0,
  scoLowTicketCash = 0,
  installmentsCash = 0,
  goalAmount = 0,
  goalCash,
  daysElapsed = 0,
  daysInMonth = 0,
  monthLabel,
}) => {
  const maxValue = Math.max(revenueClosed, cashCollected, 1);
  const revenuePercent = (revenueClosed / maxValue) * 100;
  const cashPercent = (cashCollected / maxValue) * 100;
  // Installments are recurring payments on prior deals — not new revenue collection.
  // Exclude them so the % reflects how much of THIS period's revenue has been collected.
  const cashExInstallments = cashCollected - installmentsCash;
  const collectionRate =
    revenueClosed > 0 ? Math.round((cashExInstallments / revenueClosed) * 100) : 0;

  const showBreakdown =
    highTicketCash > 0 || lowTicketCash > 0 || scoLowTicketCash > 0 || installmentsCash > 0;

  // Goal calculations
  const hasGoal = goalAmount > 0;
  const monthCash = goalCash ?? cashCollected;
  const goalPct = hasGoal ? Math.min((monthCash / goalAmount) * 100, 100) : 0;
  const remaining = hasGoal ? goalAmount - monthCash : 0;
  const daysLeft = daysInMonth - daysElapsed;

  // Compact currency for ticket breakdown (e.g. $172K)
  // Intentional integer-K for tight bar labels (see REF_design_tokens.md exceptions)
  const compactCash = (n: number) => {
    const d = toDisplayCurrency(n); // exec USD->AUD toggle (identity in USD mode)
    return d >= 1000 ? `$${Math.round(d / 1000)}K` : formatUSD(n);
  };

  const BAR_HEIGHT = 140;

  return (
    <div className="liquid-glass-hero anim-lift relative overflow-hidden flex flex-col py-6 px-6">
      {/* ── CASH HERO — THE number ── */}
      <div className="mb-1">
        <div className="token-10 uppercase text-glass-muted mb-2">Cash Collected</div>
        {getSamples ? (
          <MetricInfoIcon metricKey="cashCollected" getSamples={getSamples}>
            <span className="token-60 text-glass block">{formatUSD(cashCollected)}</span>
          </MetricInfoIcon>
        ) : (
          <span className="token-60 text-glass block">{formatUSD(cashCollected)}</span>
        )}
        {/* Revenue as context subtitle */}
        <div className="token-14 text-glass-secondary mt-2">
          {getSamples ? (
            <MetricInfoIcon metricKey="revenueClosed" getSamples={getSamples}>
              <span>
                of <span className="font-medium text-glass">{formatUSD(revenueClosed)}</span>{" "}
                revenue ({collectionRate}% collected)
              </span>
            </MetricInfoIcon>
          ) : (
            <span>
              of <span className="font-medium text-glass">{formatUSD(revenueClosed)}</span> revenue
              ({collectionRate}% collected)
            </span>
          )}
        </div>
      </div>

      {/* ── BARS + TICKET BREAKDOWN ── */}
      <div className="flex items-end flex-wrap gap-6 mt-4">
        {/* Bars */}
        <div className="flex items-end gap-4">
          {/* Revenue Bar — AMBER */}
          <div
            className="w-12 sm:w-16"
            style={{
              height: BAR_HEIGHT,
              background: "var(--glass-bg-subtle)",
              borderRadius: "5px 5px 0 0",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column-reverse",
            }}
          >
            <div
              style={{
                width: "100%",
                height: `${Math.max(revenuePercent, 3)}%`,
                background: `linear-gradient(0deg, ${hexToRgba("#E8622C", 0.2)} 0%, ${hexToRgba("#E8622C", 0.95)} 100%)`,
                boxShadow: `0 -4px 20px ${hexToRgba("#E8622C", 0.3)}`,
                borderRadius: "5px 5px 0 0",
                transition: "height 0.7s ease-out",
              }}
            />
          </div>

          {/* Cash Bar — GREEN */}
          <div
            className="w-12 sm:w-16"
            style={{
              height: BAR_HEIGHT,
              background: "var(--glass-bg-subtle)",
              borderRadius: "5px 5px 0 0",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column-reverse",
              position: "relative",
            }}
          >
            <div
              style={{
                width: "100%",
                height: `${Math.max(cashPercent, 3)}%`,
                background: `linear-gradient(0deg, ${hexToRgba(HEX.accent, 0.2)} 0%, ${hexToRgba(HEX.accent, 0.95)} 100%)`,
                boxShadow: `0 -4px 20px ${hexToRgba(HEX.accent, 0.3)}`,
                borderRadius: "5px 5px 0 0",
                transition: "height 0.7s ease-out",
                position: "relative",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: 6,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#ffffff",
                  textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                  whiteSpace: "nowrap",
                }}
              >
                {collectionRate}%
              </span>
            </div>
          </div>
        </div>

        {/* Ticket breakdown — inline beside bars */}
        {showBreakdown && (
          <div className="flex flex-col gap-2 pb-1">
            <div className="flex items-baseline gap-1.5">
              <span className="token-9 uppercase text-glass-muted w-[32px]">HT</span>
              <span
                className="token-13 font-semibold text-glass"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {compactCash(highTicketCash)}
              </span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="token-9 uppercase text-glass-muted w-[32px]">LT</span>
              {scoLowTicketCash > 0 ? (
                <Tooltip
                  content={
                    <div className="space-y-1 min-w-[150px]">
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-glass-secondary">Purchase</span>
                        <span className="font-semibold text-glass">{formatUSD(lowTicketCash)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-glass-secondary">Downsell (closer)</span>
                        <span className="font-semibold text-glass">
                          {formatUSD(scoLowTicketCash)}
                        </span>
                      </div>
                    </div>
                  }
                >
                  <span
                    data-testid="revenue-lt-cash"
                    className="token-13 font-semibold text-glass cursor-help"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {compactCash(lowTicketCash + scoLowTicketCash)}
                  </span>
                </Tooltip>
              ) : (
                <span
                  className="token-13 font-semibold text-glass"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {compactCash(lowTicketCash)}
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="token-9 uppercase text-glass-muted w-[32px]">Inst</span>
              <span
                className="token-13 font-semibold text-glass"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {compactCash(installmentsCash)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── GOAL PROGRESS BAR — full width at bottom ── */}
      {hasGoal && (
        <div className="mt-5 pt-4 border-t border-black/[0.06]">
          <div className="flex justify-between text-glass-muted mb-2 token-10l">
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {goalPct.toFixed(1)}% of {formatUSD(goalAmount)} goal
              {goalCash != null && (
                <>
                  {" "}
                  ({formatUSD(goalCash)} {monthLabel ? `in ${monthLabel}` : "this month"})
                </>
              )}
            </span>
            <span>
              {monthLabel
                ? daysLeft > 0
                  ? `${daysLeft} days left in ${monthLabel}`
                  : `in ${monthLabel}`
                : `${daysLeft} days left this month`}
            </span>
          </div>
          <div className="v2-progress-track">
            <div
              className="v2-progress-fill"
              style={{
                width: `${goalPct}%`,
                background: goalPct >= 100 ? "var(--green)" : "var(--accent)",
              }}
            />
          </div>
          <div className="text-glass-muted mt-2 token-10l">
            {formatUSD(remaining)} remaining
            {daysLeft > 0 && <> · need {formatUSD(remaining / daysLeft)}/day to hit goal</>}
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueBarChart;
